import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import {
  Outlet,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import $ from "jquery";
import {
  login,
  setIsSuccessPayment,
  setAuthUser,
  setAuthToken,
  setExpiresIn,
  setTokenCreatedAt,
  fetchFiduciary,
  setJwtToken,
  setAuthGoPeopleUserId,
  setAuthGoPeopleCommunityId,
  setAuthGoPeopleOrganizationId,
  setCommunitySwitchRequested,
  autoLoginWithTtp,
  fetchAuthUser,
  setAutoLoging,
  setCurrentPortal,
} from "../../store";
import {
  logout,
  decryptWithAES,
  authHasExpired,
  getAuthInfosFromCookie,
} from "../../utils";
import { refreshAccessToken } from "../../api";
import { getGoPeopleUserIdFromJWT } from "../../lib/jwt";
import _ from "../../i18n";
import Menu from "./Menu";
import Footer from "./Footer";
import { TTP_UA_HOME_URL } from "../../config";

// The auth cookie cannot be the only source for the GoPeople user id: several
// login paths rewrite it without userGoApiId (createAuthCookie keeps only the
// legacy fields, autoLoginWithTtp and the logged-as flow never write it). The
// JWT always carries the uuid as `user_id`, so fall back to it. Without an id
// the profile widget mounts with user_uuid = undefined and stays blank.
const resolveGoPeopleUserId = (authInfos, jwtToken) =>
  authInfos?.userGoApiId ||
  authInfos?.goPeopleUserId ||
  (jwtToken ? getGoPeopleUserIdFromJWT(jwtToken) : null);

const Layout = ({ children }) => {
  const auth = useSelector((state) => state.auth);
  const folder = useSelector((state) => state.folder);
  const hideFooter = useSelector((state) => state.params.hideFooter);
  const { navCommunity, user, communitySwitchRequested, autoLoging } = auth;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [autoLogingstate, setAutoLogingstate] = useState(false);
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (new URLSearchParams(window.location.search).get("params")) return;

        const authInfos = getAuthInfosFromCookie();

        let jwtToken =
          authInfos?.jwtToken || authInfos?.jwt || user?.jwtToken || user?.jwt;
        if (authInfos) {
          if (authInfos.token) {
            if (!jwtToken || !authInfos.refreshToken) {
              let autoLoginInfo = dispatch(autoLoginWithTtp(authInfos));
              authInfos.jwtToken = autoLoginInfo.jwtToken;
            }
          }
        }

        if (!authHasExpired(authInfos)) {
          dispatch(setAuthToken(authInfos.token));
          dispatch(setExpiresIn(authInfos.expiresIn));
          dispatch(setTokenCreatedAt(authInfos.createdAt));
          dispatch(setJwtToken(jwtToken));
          dispatch(
            setAuthGoPeopleUserId(resolveGoPeopleUserId(authInfos, jwtToken))
          );
          dispatch(
            setAuthGoPeopleCommunityId(
              authInfos.communityGoApiId || authInfos.communityGoApiId
            )
          );
          dispatch(
            setAuthGoPeopleOrganizationId(authInfos.goPeopleOrganizationId)
          );
          dispatch(fetchAuthUser(authInfos));
          dispatch(fetchFiduciary());

          return;
        }

        if (authInfos && authInfos.refreshToken) {
          try {
            const tokens = await refreshAccessToken(authInfos.refreshToken);

            const updatedAuth = {
              ...authInfos,
              jwtToken: tokens.accessToken,
              refreshToken: tokens.refreshToken,
              createdAt: Math.floor(Date.now() / 1000),
            };

            // updateAuthCookie(updatedAuth);

            dispatch(setJwtToken(tokens.accessToken));
            dispatch(setAuthToken(updatedAuth.token));
            dispatch(setExpiresIn(updatedAuth.expiresIn));
            dispatch(setTokenCreatedAt(updatedAuth.createdAt));
            dispatch(
              setAuthGoPeopleUserId(
                resolveGoPeopleUserId(updatedAuth, tokens.accessToken)
              )
            );
            dispatch(setAuthGoPeopleCommunityId(updatedAuth.communityGoApiId));

            await dispatch(fetchAuthUser(updatedAuth));
            await dispatch(fetchFiduciary());

            return;
          } catch (refreshError) {
            console.log("Refresh failed — fallback to public token");
          }
        }

        // const resp = await getPublicClientCredential();
        // const helpResp =
        //   typeof resp === "string" ? JSON.parse(resp) : resp;

        // const token = helpResp.token.access_token;
        // dispatch(setAuthToken(token));
      } catch (error) {
        console.error("Auth initialization failed:", error);
      }
    };

    initAuth();
  }, []);

  // Community/organization switching is owned by the switchCommunity /
  // updateSelectedOrganization thunks (dispatched from the nav switcher). Those
  // thunks call the backend, rebuild navCommunity, refresh the fiduciary and
  // flip communitySwitchRequested once the fiduciary is fresh. This effect only
  // reacts to that flag to route the user to the correct portal.
  useEffect(() => {
    if (!communitySwitchRequested) return;

    if (!folder.fiduciary) return;

    if (folder.fiduciary.uaMode === "COMMUNITY") {
      dispatch(setCurrentPortal("SETTINGS"));
      navigate("/settings");
    } else {
      dispatch(setCurrentPortal("COLLABORATOR"));
      navigate("/collaborator");
    }

    dispatch(setCommunitySwitchRequested(false));
  }, [communitySwitchRequested, folder.fiduciary]);
  useEffect(() => {
    if (!auth.user) return;

    if (folder.fetching) return;

    if (!folder.fetched) return;

    if (!folder.fiduciary) {
      if (!autoLoging && !autoLogingstate) {
        if (
          auth.user.pending_invitation &&
          auth.user.pending_invitation.token
        ) {
          navigate(`/invitation?token=${auth.user.pending_invitation.token}`);
          return;
        }
        // A missing fiduciary is NOT a signal to create a cabinet: a community
        // member whose organization has not resolved yet also lands here. Founders
        // reach cabinet creation through registration and the CREATED/DELETED
        // branch below, so we never auto-redirect to /create-office from here.
      }
      return;
    }

    if (auth.user && auth.user.role && !auth.autoLoging && !autoLogingstate) {
      // if (
      //   folder.fetched &&
      //   folder.fiduciary &&
      //   folder.fiduciary.subscription &&
      //   !["PENDING", "IN_PROGRESS"].includes(folder.fiduciary.subscription) &&
      //   location.pathname !== "/account-activation"
      // ) {
      //   navigate("/account-suspended");
      //   return null;
      // }
      if (
        folder.fetched &&
        folder.fiduciary &&
        folder.fiduciary?.uaHasAccess === false &&
        !["/subscriptions", "/invoices"].includes(location.pathname)
      ) {
        navigate("/account-suspended");
        return null;
      }
      if (auth.user.role.typeStatus === "FOLDER") {
        navigate("/client");
      } else {
        if (folder.fiduciary && folder.fiduciary.uaMode === "COMMUNITY") {
          // navigate("/settings");
          // } else {
          //   navigate("/collaborator");
        }
      }
      const isOnProfile = location.pathname === "/profile";
      if (auth.user.role.type === "OFFICIAL" && !isOnProfile) {
        navigate({
          pathname: "/collaborator",
          search: searchParams.toString(),
        });
        return null;
      } else if (auth.user.role.typeStatus === "FOLDER" && !isOnProfile) {
        navigate({
          pathname: "/client",
          search: searchParams.toString(),
        });
        return null;
      }
    }
    if (auth.user && folder.fetched && !autoLoging && !autoLogingstate) {
      if (!folder.fiduciary) {
        if (
          auth.user.pending_invitation &&
          auth.user.pending_invitation.token
        ) {
          navigate(`/invitation?token=${auth.user.pending_invitation.token}`);
          return;
        }
        // A missing fiduciary is NOT a signal to create a cabinet: a community
        // member whose organization has not resolved yet also lands here. Founders
        // reach cabinet creation through registration and the CREATED/DELETED
        // branch below, so we never auto-redirect to /create-office from here.
      } else if (folder.fiduciary) {
        if (
          folder.fiduciary.uaStatus === "CREATED" ||
          folder.fiduciary.uaStatus === "DELETED"
        ) {
          navigate("/create-office");
        } else if (folder.fiduciary.uaStatus === "PENDING") {
          navigate("/pending");
        } else if (folder.fiduciary.uaStatus === "VALIDATED") {
          if (searchParams.get("ps")) {
            dispatch(setIsSuccessPayment(true));
          }
          if (folder.fiduciary.onBoardingStep === "CREATED") {
            navigate("/welcome");
          } else if (folder.fiduciary.onBoardingStep !== "COMPLETE") {
            navigate("/join");
          }
        }
      }
    }
  }, [auth, folder]);

  useEffect(() => {
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      function (error) {
        if (error.response?.status === 401) {
          dispatch(setAuthUser(null));
          logout(false);
          // navigate("/");
          window.location.assign(`${TTP_UA_HOME_URL}`);
          return Promise.reject(error);
        }

        return Promise.reject(error);
      }
    );

    let authInfos = null;
    const params = new URLSearchParams(window.location.search).get("params");
    if (params) {
      const decryptedParams = JSON.parse(decryptWithAES(params));
      if (
        decryptedParams["token"] &&
        decryptedParams["userId"] &&
        decryptedParams["tokenCreatedAt"] &&
        decryptedParams["tokenExpiresIn"]
      ) {
        dispatch(setAuthUser(null));
        logout(false);
        authInfos = {
          id: decryptedParams["userId"],
          token: decryptedParams["token"],
          createdAt: decryptedParams["tokenCreatedAt"],
          expiresIn: decryptedParams["tokenExpiresIn"],
        };
        dispatch(setAuthToken(authInfos.token));
        if (authInfos) {
          dispatch(
            autoLoginWithTtp({
              ...authInfos,
              fiduciaryId: decryptedParams["id"] ?? null,
            })
          );
        }
        dispatch(setExpiresIn(authInfos.expiresIn));
        dispatch(setTokenCreatedAt(authInfos.createdAt));
      }
    }

    if (!authInfos) {
      dispatch(login());
    }

    if (!auth.user) {
      $("#app-loader")
        .fadeOut()
        .promise()
        .done(() => $("#app-loader").remove());
    }
  }, []);

  if (!folder.fetched && !folder.fetching) {
    return null;
  }

  const withoutFooterURL = [
    "/join",
    "/create-office",
    "/welcome",
    "/account-activation",
  ];

  if (hideFooter && location.pathname === "/collaborator") {
    withoutFooterURL.push(location.pathname);
  }

  return (
    <div>
      <Menu />
      <Outlet />
      {!withoutFooterURL.includes(location.pathname) &&
        folder.fiduciary &&
        folder.fiduciary.id !== 4 && <Footer />}
    </div>
  );
};

export default Layout;
